"use client";

export {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

import { DialogContent as ShadcnDialogContent } from "@/components/ui/dialog";
import { cn } from "@/lib/utils/general-utils";
import React from "react";

const sizeClasses = {
  sm: "sm:max-w-sm",
  md: "sm:max-w-md",
  lg: "sm:max-w-lg",
  xl: "sm:max-w-xl",
  "2xl": "sm:max-w-2xl",
  "3xl": "sm:max-w-3xl",
  "4xl": "sm:max-w-4xl",
  "5xl": "sm:max-w-5xl",
  responsive: "max-w-[90vw] lg:max-w-5xl xl:max-w-6xl",
  fullscreen: "max-w-[95vw] sm:max-w-[90vw] lg:max-w-6xl",
} as const;

type DialogSize = keyof typeof sizeClasses;

function DialogContent({
  className,
  size = "2xl",
  ...props
}: React.ComponentProps<typeof ShadcnDialogContent> & {
  size?: DialogSize;
}) {
  return (
    <ShadcnDialogContent
      className={cn(
        "!flex !flex-col gap-0 p-0 min-h-0 max-h-[85vh] max-h-[85dvh] overflow-hidden",
        sizeClasses[size],
        className,
      )}
      {...props}
    />
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn(
        "shrink-0 flex flex-col gap-1.5 border-b border-border px-6 py-4",
        className,
      )}
      {...props}
    />
  );
}

function DialogBody({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-body"
      className={cn(
        "flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden px-6 py-6",
        className,
      )}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "shrink-0 border-t border-border bg-muted/50 px-6 py-4",
        className,
      )}
      {...props}
    />
  );
}

export { DialogContent, DialogHeader, DialogBody, DialogFooter };
export type { DialogSize };
